import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, FolderKanban, ChevronDown, ChevronRight, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { VOLUNTEER_ROLES, VOLUNTEER_ROLE_LABELS, TASK_STATUS } from "@/lib/roles";
import { FileUpload } from "@/components/FileUpload";
import TaskDetailDialog, { DueBadge } from "@/components/volunteer/TaskDetailDialog";

const selectCls = "w-full rounded-xl border border-[#e4d8c4] bg-white px-3 py-2.5 text-sm text-[#00334a]";
const emptyProject = { name: "", description: "", campaign_id: "", start_date: "", end_date: "", status: "planning", seeking_volunteers: false };
const STATUS_CLS = { planning: "bg-[#efe9dd] text-[#5a5849]", active: "bg-[#f6ecd7] text-[#9c6a2e]", done: "bg-[#e8e7d6] text-[#5f6a48]" };

export default function ProjectsManage() {
  const [projects, setProjects] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [pOpen, setPOpen] = useState(false);
  const [pForm, setPForm] = useState(emptyProject);
  const [pEditing, setPEditing] = useState(null);
  const [tOpen, setTOpen] = useState(false);
  const [tForm, setTForm] = useState({ title: "", description: "", due_date: "", suited_role: "", assignee_id: "" });
  const [busy, setBusy] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const loadProjects = useCallback(() => { api.get("/projects").then(({ data }) => setProjects(data)); }, []);
  useEffect(() => {
    loadProjects();
    api.get("/campaigns").then(({ data }) => setCampaigns(data)).catch(() => {});
    api.get("/volunteers/directory").then(({ data }) => {
      setVolunteers(data.flatMap((g) => g.volunteers.map((v) => ({ id: v.id, name: v.name, role: g.label }))));
    }).catch(() => {});
  }, [loadProjects]);

  const loadTasks = (pid) => api.get(`/tasks?project_id=${pid}`).then(({ data }) => setTasks(data));
  const loadAttachments = (pid) => api.get(`/attachments?project_id=${pid}`).then(({ data }) => setAttachments(data)).catch(() => setAttachments([]));
  const toggle = (pid) => { if (expanded === pid) { setExpanded(null); } else { setExpanded(pid); loadTasks(pid); loadAttachments(pid); } };
  const delAttachment = async (id) => { await api.delete(`/files/${id}`); toast.success("Removed"); loadAttachments(expanded); };

  const saveProject = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      if (pEditing) await api.put(`/projects/${pEditing}`, pForm);
      else await api.post("/projects", pForm);
      toast.success("Project saved"); setPOpen(false); loadProjects();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const delProject = async (id) => { if (!window.confirm("Delete project and its tasks?")) return; await api.delete(`/projects/${id}`); toast.success("Deleted"); loadProjects(); };

  const saveTask = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/tasks", { project_id: expanded, title: tForm.title, description: tForm.description,
        due_date: tForm.due_date || null, suited_role: tForm.suited_role || null, assignee_id: tForm.assignee_id || null });
      toast.success("Task created"); setTOpen(false); loadTasks(expanded); loadProjects();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const assign = async (id, assignee_id) => { await api.post(`/tasks/${id}/assign`, { assignee_id: assignee_id || null }); toast.success("Task assigned"); loadTasks(expanded); };
  const reopen = async (id) => { await api.post(`/tasks/${id}/reopen`); toast.success("Task reopened"); loadTasks(expanded); };
  const delTask = async (id) => { await api.delete(`/tasks/${id}`); toast.success("Task deleted"); loadTasks(expanded); loadProjects(); };

  return (
    <div className="space-y-6" data-testid="volunteer-projects">
      <div className="flex items-center justify-between">
        <div>
          <span className="hf-eyebrow">Volunteer workspace</span>
          <h1 className="mt-1 font-heading text-3xl font-semibold text-[#00334a]">Projects & Tasks</h1>
        </div>
        <Button onClick={() => { setPEditing(null); setPForm(emptyProject); setPOpen(true); }} className="rounded-full hf-btn-coral font-bold" data-testid="project-create-button">
          <Plus className="mr-2 h-4 w-4" /> New project
        </Button>
      </div>

      {projects.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-[#e4d8c4] bg-white p-12 text-center">
          <FolderKanban className="mx-auto h-10 w-10 text-[#e4d8c4]" />
          <p className="mt-3 font-semibold text-[#00334a]">No projects yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {projects.map((p) => (
            <div key={p.id} className="rounded-2xl border border-[#e4d8c4] bg-white" data-testid={`project-${p.id}`}>
              <div className="flex items-center gap-3 p-4">
                <button onClick={() => toggle(p.id)} className="text-[#3f6670]" data-testid={`project-toggle-${p.id}`}>
                  {expanded === p.id ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                </button>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-heading text-lg font-semibold text-[#00334a]">{p.name}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${STATUS_CLS[p.status]}`}>{p.status}</span>
                    {p.seeking_volunteers && <span className="rounded-full bg-[#efe6d6] px-2 py-0.5 text-[10px] font-bold text-[#3f6670]">Seeking volunteers</span>}
                  </div>
                  <div className="text-xs text-[#7c7a6a]">{p.member_count ?? p.members?.length ?? 0} members · {p.open_task_count} open of {p.task_count} tasks</div>
                </div>
                <Button size="icon" variant="ghost" onClick={() => { setPEditing(p.id); setPForm({ ...emptyProject, ...p }); setPOpen(true); }} className="h-8 w-8 text-[#3f6670]" data-testid={`project-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => delProject(p.id)} className="h-8 w-8 text-[#c97049]" data-testid={`project-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>

              {expanded === p.id && (
                <div className="border-t border-[#efe6d6] p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-wide text-[#7c7a6a]">Tasks</span>
                    <Button size="sm" onClick={() => { setTForm({ title: "", description: "", due_date: "", suited_role: "", assignee_id: "" }); setTOpen(true); }} className="rounded-full bg-[#00334a] font-bold hover:bg-[#00263a]" data-testid="task-create-button">
                      <Plus className="mr-1.5 h-3.5 w-3.5" /> Add task
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {tasks.length === 0 && <div className="text-sm text-[#7c7a6a]">No tasks yet.</div>}
                    {tasks.map((t) => (
                      <div key={t.id} className="flex flex-wrap items-center gap-2 rounded-xl bg-[#f8f3e9] p-3" data-testid={`task-${t.id}`}>
                        <div className="min-w-0 flex-1">
                          <button onClick={() => setDetailId(t.id)} className="text-left font-semibold text-[#00334a] hover:text-[#c97049]" data-testid={`task-open-${t.id}`}>{t.title}</button>
                          <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-[#7c7a6a]">
                            <span>{t.suited_role ? `${t.suited_role_label} · ` : ""}{t.assignee_name ? `Assigned: ${t.assignee_name}` : "Up for grabs"}{t.due_date ? ` · due ${t.due_date}` : ""}</span>
                            <DueBadge task={t} />
                          </div>
                        </div>
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${TASK_STATUS[t.status]?.cls}`}>{TASK_STATUS[t.status]?.label}</span>
                        <select value={t.assignee_id || ""} onChange={(e) => assign(t.id, e.target.value)} className="rounded-lg border border-[#e4d8c4] bg-white px-2 py-1 text-xs text-[#00334a]" data-testid={`task-assign-${t.id}`}>
                          <option value="">Unassigned</option>
                          {volunteers.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                        </select>
                        <Button size="icon" variant="ghost" onClick={() => reopen(t.id)} className="h-7 w-7 text-[#3f6670]" data-testid={`task-reopen-${t.id}`}><RotateCcw className="h-3.5 w-3.5" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => delTask(t.id)} className="h-7 w-7 text-[#c97049]" data-testid={`task-delete-${t.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 border-t border-[#efe6d6] pt-3">
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-xs font-semibold uppercase tracking-wide text-[#7c7a6a]">Attachments</span>
                      <FileUpload scope="attachment" projectId={p.id} label="Attach file" testId={`project-attach-${p.id}`}
                        onUploaded={() => loadAttachments(p.id)} />
                    </div>
                    <div className="space-y-1.5">
                      {attachments.length === 0 && <div className="text-sm text-[#7c7a6a]">No files attached.</div>}
                      {attachments.map((f) => (
                        <div key={f.id} className="flex items-center justify-between rounded-lg bg-[#f8f3e9] px-3 py-2 text-sm" data-testid={`attachment-${f.id}`}>
                          <a href={`${f.url}?auth=${localStorage.getItem("hf_token")}`} target="_blank" rel="noopener noreferrer" className="truncate font-semibold text-[#00334a] hover:text-[#c97049]">{f.original_filename}</a>
                          <Button size="icon" variant="ghost" onClick={() => delAttachment(f.id)} className="h-7 w-7 text-[#c97049]" data-testid={`attachment-delete-${f.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Project dialog */}
      <Dialog open={pOpen} onOpenChange={setPOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="project-dialog">
          <DialogHeader><DialogTitle className="font-heading">{pEditing ? "Edit project" : "New project"}</DialogTitle></DialogHeader>
          <form onSubmit={saveProject} className="space-y-4">
            <div className="space-y-1.5"><Label>Name</Label><Input value={pForm.name} onChange={(e) => setPForm({ ...pForm, name: e.target.value })} required data-testid="project-name-input" /></div>
            <div className="space-y-1.5"><Label>Description</Label><Textarea value={pForm.description} onChange={(e) => setPForm({ ...pForm, description: e.target.value })} rows={2} /></div>
            <div className="space-y-1.5">
              <Label>Linked campaign (optional)</Label>
              <select value={pForm.campaign_id || ""} onChange={(e) => setPForm({ ...pForm, campaign_id: e.target.value })} className={selectCls} data-testid="project-campaign-select">
                <option value="">General — no campaign</option>
                {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <p className="text-xs text-[#7c7a6a]">Volunteers on this project can watch this campaign's progress.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Start date</Label><Input type="date" value={pForm.start_date || ""} onChange={(e) => setPForm({ ...pForm, start_date: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>End date</Label><Input type="date" value={pForm.end_date || ""} onChange={(e) => setPForm({ ...pForm, end_date: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Status</Label>
                <select value={pForm.status} onChange={(e) => setPForm({ ...pForm, status: e.target.value })} className={selectCls} data-testid="project-status-select">
                  <option value="planning">Planning</option><option value="active">Active</option><option value="done">Done</option>
                </select>
              </div>
              <label className="mt-6 flex items-center gap-2 text-sm font-semibold text-[#3f6670]">
                <input type="checkbox" checked={pForm.seeking_volunteers} onChange={(e) => setPForm({ ...pForm, seeking_volunteers: e.target.checked })} className="h-4 w-4 accent-[#c97049]" data-testid="project-seeking-checkbox" />
                Seeking volunteers
              </label>
            </div>
            <DialogFooter><Button type="submit" disabled={busy} className="rounded-full hf-btn-coral font-bold" data-testid="project-save-button">{busy ? "Saving…" : "Save project"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Task dialog */}
      <Dialog open={tOpen} onOpenChange={setTOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="task-dialog">
          <DialogHeader><DialogTitle className="font-heading">New task</DialogTitle></DialogHeader>
          <form onSubmit={saveTask} className="space-y-4">
            <div className="space-y-1.5"><Label>Title</Label><Input value={tForm.title} onChange={(e) => setTForm({ ...tForm, title: e.target.value })} required data-testid="task-title-input" /></div>
            <div className="space-y-1.5"><Label>Description</Label><Textarea value={tForm.description} onChange={(e) => setTForm({ ...tForm, description: e.target.value })} rows={2} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Due date</Label><Input type="date" value={tForm.due_date} onChange={(e) => setTForm({ ...tForm, due_date: e.target.value })} /></div>
              <div className="space-y-1.5">
                <Label>Suited role</Label>
                <select value={tForm.suited_role} onChange={(e) => setTForm({ ...tForm, suited_role: e.target.value })} className={selectCls} data-testid="task-suited-select">
                  <option value="">Anyone</option>
                  {VOLUNTEER_ROLES.map((r) => <option key={r} value={r}>{VOLUNTEER_ROLE_LABELS[r]}</option>)}
                </select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Assign to (optional)</Label>
              <select value={tForm.assignee_id} onChange={(e) => setTForm({ ...tForm, assignee_id: e.target.value })} className={selectCls} data-testid="task-assignee-select">
                <option value="">Leave up for grabs</option>
                {volunteers.map((v) => <option key={v.id} value={v.id}>{v.name} ({v.role})</option>)}
              </select>
            </div>
            <DialogFooter><Button type="submit" disabled={busy} className="rounded-full hf-btn-coral font-bold" data-testid="task-save-button">{busy ? "Saving…" : "Create task"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <TaskDetailDialog taskId={detailId} open={!!detailId} onOpenChange={(o) => !o && setDetailId(null)}
        onChanged={() => { if (expanded) loadTasks(expanded); loadProjects(); }} />
    </div>
  );
}
